import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useAuth } from '@/hooks/useAuth';
import api from '@/lib/api';
import { ScheduleTab } from '@/components/schedule/ScheduleTab';
import { TeamMemberCard } from './TeamMemberCard';
import { PaymentAlert } from './PaymentAlert';
import { AIChatBot } from './AIChatBot';
import { PlannedVsActualView } from './PlannedVsActualView';

export function GuardianDashboardTab() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // Fetch monthly hours limit
  const { data: limitData } = useQuery({
    queryKey: ['settings', 'monthly_hours_limit'],
    queryFn: async () => {
      const res = await api.get('/settings/monthly_hours_limit');
      return res.data;
    },
    staleTime: 1000 * 10,
  });

  // Fetch monthly summary
  const { data: summaryData } = useQuery({
    queryKey: ['monthly-summary', currentYear, currentMonth],
    queryFn: async () => {
      const params = new URLSearchParams({ year: String(currentYear), month: String(currentMonth) });
      const res = await api.get(`/timeslots/monthly-summary?${params}`);
      return res.data;
    },
    staleTime: 1000 * 10,
    refetchInterval: 10000,
  });

  // Fetch team members
  const { data: teamUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await api.get('/users');
      const data = res.data;
      return data.users || data || [];
    },
    staleTime: 1000 * 10,
  });

  // Fetch all timeslots for current month to compute per-person hours
  const monthStart = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
  const lastDay = new Date(currentYear, currentMonth, 0).getDate();
  const monthEnd = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  const { data: monthSlots = [] } = useQuery({
    queryKey: ['timeslots', monthStart, monthEnd],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: monthStart,
        end_date: monthEnd,
        include_caretaker: 'true',
      });
      const res = await api.get(`/timeslots?${params}`);
      return res.data.slots || [];
    },
    staleTime: 1000 * 10,
    refetchInterval: 10000,
  });

  // Fetch availability windows for current month (includes auto-assign info)
  const { data: monthAvailability = [] } = useQuery({
    queryKey: ['availability', monthStart, monthEnd],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: monthStart,
        end_date: monthEnd,
      });
      const res = await api.get(`/availability/windows?${params}`);
      const flattened: any[] = [];
      (res.data.availability || []).forEach((day: any) => {
        (day.windows || []).forEach((w: any) => {
          if (w.auto_assign_user_id) {
            flattened.push({
              date: day.date,
              start_time: w.start,
              end_time: w.end,
              auto_assign_user_id: w.auto_assign_user_id,
            });
          }
        });
      });
      return flattened;
    },
    staleTime: 1000 * 10,
    refetchInterval: 10000,
  });

  const monthlyLimit = parseFloat(limitData?.value || '80');
  const usedHours = summaryData?.used_hours || 0;
  const projectedHours = summaryData?.projected_hours || 0;

  // Editable limit state
  const [editLimit, setEditLimit] = useState('');
  const [isSavingLimit, setIsSavingLimit] = useState(false);
  useEffect(() => {
    if (limitData?.value) setEditLimit(limitData.value);
  }, [limitData?.value]);

  const handleSaveLimit = async () => {
    const val = parseFloat(editLimit);
    if (isNaN(val) || val <= 0) return;
    setIsSavingLimit(true);
    try {
      await api.put('/settings/monthly_hours_limit', { value: String(val) });
      queryClient.invalidateQueries({ queryKey: ['settings', 'monthly_hours_limit'] });
    } finally {
      setIsSavingLimit(false);
    }
  };

  // Compute per-person hours (real slots + auto-assign virtual hours from today onward)
  const today = format(now, 'yyyy-MM-dd');
  const personHours = useMemo(() => {
    const map = new Map<string, number>();
    // Count real time slots
    (monthSlots as any[]).forEach((slot: any) => {
      if (slot.status === 'cancelled') return;
      const existing = map.get(slot.caretaker_id) || 0;
      map.set(slot.caretaker_id, existing + (slot.hours_worked || 0));
    });
    // Add auto-assign window hours from today onward where no real slot exists
    for (const w of monthAvailability as any[]) {
      if (w.date < today) continue;
      const hasRealSlot = (monthSlots as any[]).some(
        (s: any) => s.date === w.date && s.start_time === w.start_time && s.end_time === w.end_time && s.status !== 'cancelled'
      );
      if (!hasRealSlot) {
        const [sh, sm] = w.start_time.split(':').map(Number);
        const [eh, em] = w.end_time.split(':').map(Number);
        const hours = (eh * 60 + em - sh * 60 - sm) / 60;
        const existing = map.get(w.auto_assign_user_id) || 0;
        map.set(w.auto_assign_user_id, existing + hours);
      }
    }
    return map;
  }, [monthSlots, monthAvailability, today]);

  const assignableTeam = (teamUsers as any[]).filter((u: any) => u.role === 'caretaker' || u.role === 'family');

  const usedPct = monthlyLimit > 0 ? Math.min((usedHours / monthlyLimit) * 100, 100) : 0;
  const projectedPct = monthlyLimit > 0 ? Math.min((projectedHours / monthlyLimit) * 100, 100) : 0;

  return (
    <div data-testid="guardian-dashboard-tab" className="space-y-6">
      {/* Payment Alert */}
      <PaymentAlert />

      {/* AI Assistant */}
      <AIChatBot />

      {/* Hours Overview */}
      <div className="bg-card rounded-xl border border-slate-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">
          Hours Overview - {format(now, 'MMMM yyyy')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Used Hours */}
          <div data-testid="container-hours-used" className="bg-slate-800 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Used Hours</div>
            <div className="text-2xl font-bold text-white">{usedHours.toFixed(1)}<span className="text-gray-500 text-sm">/{monthlyLimit} ({Math.round(usedPct)}%)</span></div>
            <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${usedPct}%` }} />
            </div>
          </div>
          {/* Projected Hours */}
          <div data-testid="container-hours-projected" className="bg-slate-800 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Projected Hours</div>
            <div className="text-2xl font-bold text-blue-400">{projectedHours.toFixed(1)}<span className="text-gray-500 text-sm">/{monthlyLimit} ({Math.round(projectedPct)}%)</span></div>
            <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${projectedPct}%` }} />
            </div>
          </div>
          {/* Monthly Limit */}
          <div data-testid="container-hours-limit" className="bg-slate-800 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Monthly Limit</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={editLimit}
                onChange={(e) => setEditLimit(e.target.value)}
                data-testid="input-monthly-hours-limit"
                className="w-24 px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1"
              />
              <span className="text-gray-400 text-sm">hrs</span>
              <button
                onClick={handleSaveLimit}
                disabled={isSavingLimit || editLimit === limitData?.value}
                data-testid="clickable-save-monthly-limit"
                className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
              >
                {isSavingLimit ? '...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Team Overview */}
      <div className="bg-card rounded-xl border border-slate-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Team Overview</h2>
        {assignableTeam.length === 0 ? (
          <div className="text-center py-6 text-gray-400">No caretakers or family members registered yet.</div>
        ) : (
          <div className="space-y-3">
            {assignableTeam.map((member: any) => (
              <TeamMemberCard key={member.id} member={member} monthHours={personHours.get(member.id) || 0} />
            ))}
          </div>
        )}
      </div>

      {/* Planned vs Actual */}
      <PlannedVsActualView />

      {/* Compact Weekly Schedule */}
      <ScheduleTab compact />
    </div>
  );
}
